(() => {
  const overlayChanged = () => document.dispatchEvent(new Event('rh:overlay-change'));
  const carousels = new Map();
  let catalogController, searchCleanup, dismissSearch;
  const closeCatalog = (immediate = false, focus = true) => catalogController?.close(immediate, focus);

  function initCarousel() {
    carousels.forEach((cleanup, element) => { if (!element.isConnected) { cleanup(); carousels.delete(element); } });
    document.querySelectorAll('[data-rh-carousel]').forEach(carousel => {
      if (carousels.has(carousel)) return;
      const track = carousel.querySelector('[data-rh-slides]');
      const slides = [...track.querySelectorAll('.rh-slide')];
      const lifecycle = new AbortController();
      const signal = lifecycle.signal;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let current = 0, visible = false, paused = reduced.matches, timer, frame, settle;
      const looping = slides.length > 1 && carousel.dataset.designMode !== 'true';
      const clone = looping ? slides[0].cloneNode(true) : null;
      if (clone) {
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        clone.setAttribute('aria-hidden','true'); clone.inert = true; clone.dataset.rhClone = 'true'; track.append(clone);
      }
      const settleSlide = () => {
        if (frame) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        if (looping && index === slides.length) track.scrollLeft = 0;
        current = index % slides.length;
        slides.forEach((slide, i) => { slide.inert = i !== current; });
      };
      const schedule = () => {
        clearTimeout(timer);
        if (slides.length < 2 || !carousel.isConnected || carousel.dataset.designMode === 'true' || carousel.dataset.autoplay !== 'true' || paused || reduced.matches || !visible || document.hidden || document.querySelector('dialog[open]') || track.contains(document.activeElement) || (matchMedia('(hover:hover)').matches && carousel.matches(':hover'))) return;
        timer = setTimeout(() => go(current + 1), Number(carousel.dataset.interval) || 6000);
      };
      const go = index => {
        if (!slides.length) return;
        cancelAnimationFrame(frame); frame = 0; clearTimeout(settle);
        const next = (index + slides.length) % slides.length;
        const target = looping && index === slides.length ? slides.length : next;
        const start = track.scrollLeft, delta = target * track.clientWidth - start;
        current = next;
        const finish = () => { frame = 0; track.classList.remove('is-moving'); settleSlide(); schedule(); };
        if (reduced.matches || Math.abs(delta) < 1) { track.scrollLeft = target * track.clientWidth; finish(); return; }
        track.classList.add('is-moving');
        const began = performance.now();
        const tick = now => {
          const progress = Math.min(1,(now - began) / 650);
          track.scrollLeft = start + delta * (1 - Math.pow(1 - progress,4));
          if (progress < 1) frame = requestAnimationFrame(tick); else finish();
        };
        frame = requestAnimationFrame(tick);
      };
      const pause = () => { paused = true; clearTimeout(timer); };
      track.addEventListener('pointerdown', () => { pause(); cancelAnimationFrame(frame); frame = 0; track.classList.remove('is-moving'); }, {passive:true,signal});
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault(); pause();
        go(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
      }, {signal});
      track.addEventListener('scroll', () => { clearTimeout(settle); settle = setTimeout(settleSlide,120); }, {passive:true,signal});
      track.addEventListener('scrollend',settleSlide,{signal});
      slides.forEach((slide, index) => { slide.inert = index !== 0; });
      carousel.addEventListener('mouseenter', () => clearTimeout(timer), {signal});
      carousel.addEventListener('mouseleave', schedule, {signal});
      carousel.addEventListener('focusin', pause, {signal});
      carousel.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('.rh-slide');
        const index = slides.indexOf(slide);
        if (index >= 0) { pause(); go(index); }
      }, {signal});
      document.addEventListener('visibilitychange', schedule, {signal});
      document.addEventListener('rh:overlay-change', schedule, {signal});
      reduced.addEventListener('change', () => { paused = reduced.matches; schedule(); }, {signal});
      const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule(); }, {threshold:.25});
      observer.observe(carousel);
      carousels.set(carousel, () => { clearTimeout(timer); clearTimeout(settle); cancelAnimationFrame(frame); clone?.remove(); track.classList.remove('is-moving'); observer.disconnect(); lifecycle.abort(); });
    });
  }

  // Rasterize only the visible catalog once. No DOM clones, pixel readback or dependency.
  async function catalogSnapshot(surface, width, height, scale) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(width*scale); canvas.height = Math.ceil(height*scale);
    const ctx = canvas.getContext('2d'); ctx.scale(scale,scale);
    ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';
    const origin = surface.getBoundingClientRect();
    const elements = [surface,...surface.querySelectorAll('*')];
    const images = [...surface.querySelectorAll('img')].filter(img => {const rect=img.getBoundingClientRect();return rect.width && rect.height && rect.bottom>origin.top && rect.top<origin.top+height;});
    images.forEach(img => {img.loading='eager';});
    // Never hold the interaction for offscreen/lazy image decoders.
    await Promise.race([Promise.all(images.filter(img=>img.complete).map(img => img.decode?.().catch(() => {}))),new Promise(resolve => requestAnimationFrame(resolve))]);
    const styles = new Map();
    const draw = async element => {
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height || rect.bottom<=origin.top || rect.top>=origin.top+height) return;
      const style = getComputedStyle(element); styles.set(element,style);
      const x=rect.left-origin.left, y=rect.top-origin.top;
      ctx.save();
      // Respect the scrolled body and its clipping ancestors.
      for(let parent=element.parentElement;parent && parent!==surface;parent=parent.parentElement){
        const css=styles.get(parent)||getComputedStyle(parent);
        if (/(hidden|auto|scroll)/.test(css.overflowY)) {
          const clip=parent.getBoundingClientRect();ctx.beginPath();ctx.rect(clip.left-origin.left,clip.top-origin.top,clip.width,clip.height);ctx.clip();
        }
      }
      if (style.backgroundColor!=='rgba(0, 0, 0, 0)') {
        ctx.fillStyle=style.backgroundColor;ctx.beginPath();ctx.roundRect(x,y,rect.width,rect.height,Math.min(parseFloat(style.borderTopLeftRadius)||0,rect.width/2,rect.height/2));ctx.fill();
      }
      if(element.tagName==='IMG' && element.complete && element.naturalWidth){
        const ratio=Math.min(rect.width/element.naturalWidth,rect.height/element.naturalHeight);
        const w=element.naturalWidth*ratio,h=element.naturalHeight*ratio;
        ctx.drawImage(element,x+(rect.width-w)/2,y+(rect.height-h)/2,w,h);
      } else if(element.tagName.toLowerCase()==='svg') {
        const svg=element.cloneNode(true);svg.setAttribute('xmlns','http://www.w3.org/2000/svg');
        svg.setAttribute('color',style.color);svg.setAttribute('width',rect.width);svg.setAttribute('height',rect.height);
        const img=new Image();img.src='data:image/svg+xml;charset=utf-8,'+encodeURIComponent(svg.outerHTML.replace(/currentColor/g,style.color));
        try{await img.decode();
          const transform=style.transform.match(/^matrix\(([^)]+)\)$/);
          if(transform){ctx.translate(x+rect.width/2,y+rect.height/2);ctx.transform(...transform[1].split(',').map(Number));ctx.drawImage(img,-rect.width/2,-rect.height/2,rect.width,rect.height);}
          else ctx.drawImage(img,x,y,rect.width,rect.height);
        }catch{}
      } else if(element.tagName==='INPUT') {
        ctx.font=`${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;ctx.fillStyle=style.color;ctx.textBaseline='middle';
        ctx.fillText(element.value||element.placeholder,x+(parseFloat(style.paddingLeft)||12),y+rect.height/2,rect.width-30);
      } else {
        ctx.font=`${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        ctx.fillStyle=style.color;ctx.textBaseline='middle';
        for(const node of element.childNodes){
          if(node.nodeType!==Node.TEXT_NODE || !node.textContent.trim()) continue;
          const text=node.textContent,range=document.createRange();
          for(const word of text.matchAll(/\S+/g)){
            range.setStart(node,word.index);range.setEnd(node,word.index+word[0].length);
            const box=range.getBoundingClientRect();if(!box.width)continue;
            ctx.fillText(word[0],box.left-origin.left,box.top-origin.top+box.height/2);
          }
        }
      }
      ctx.restore();
    };
    for(const element of elements) await draw(element);
    return canvas;
  }
  // Scanline geometry adapted from the merchant-supplied renderGenie example.
  function genieRow(time, minimize, row, width, height, dock) {
    const ratio=row/height;
    // One continuous clock: the leading edge arrives first, then the body follows.
    const progress=minimize?1-time:time;
    const xStart=ratio*.55, xEnd=.65+ratio*.35;
    const x=Math.max(0,Math.min(1,(progress-xStart)/(xEnd-xStart)));
    const spread=x*x;
    const xEase=spread*spread*(3-2*spread);
    const yStart=ratio*.15, yEnd=.65+ratio*.35;
    const travel=Math.max(0,Math.min(1,(progress-yStart)/(yEnd-yStart)));
    // Feather only the arrival; keep the established travel and wave order.
    const arrival=Math.max(0,(travel-.88)/.12);
    const yEase=travel<=.88?travel:.88+.12*(arrival+arrival*arrival-arrival*arrival*arrival);
    const dockWidth=dock.width||0, dockHeight=dock.height||0;
    const dockLeft=dock.x-dockWidth/2, dockY=dock.y-dockHeight/2+ratio*dockHeight;
    return {left:dockLeft*(1-xEase),width:dockWidth+(width-dockWidth)*xEase,y:dockY+(row-dockY)*yEase};
  }
  function initCatalog() {
    const catalog = document.querySelector('[data-rh-catalog-panel]');
    document.querySelectorAll('[data-rh-catalog]').forEach(trigger => {
      if (!catalog) { trigger.removeAttribute('aria-haspopup'); trigger.removeAttribute('aria-controls'); trigger.removeAttribute('aria-expanded'); return; }
      trigger.setAttribute('aria-haspopup','dialog');
      trigger.setAttribute('aria-controls',catalog.id);
      trigger.setAttribute('aria-expanded',String(catalog.open));
    });
    if (catalogController?.element === catalog) return;
    catalogController?.destroy();
    catalogController = null;
    if (!catalog) return;
    const lifecycle = new AbortController();
    const signal = lifecycle.signal;
    const mobile = matchMedia('(max-width: 767px)');
    let trigger, closing = false, fallback, motionFrame, motionVersion=0, motionCanvas, motionSource, motionProgress=0, launchTarget, snapshotDirty=true;
    const menuTabs = [...catalog.querySelectorAll('[data-rh-menu-tab]')];
    const sheetTitle = catalog.querySelector('.rh-catalog__header h2');
    const defaultTitle = sheetTitle.textContent;
    const backButton = catalog.querySelector('.rh-back');
    const defaultBackLabel = backButton.getAttribute('aria-label');
    const sheetBody = catalog.querySelector('.rh-catalog__body');
    let activeBranch;
    const branchStack = [];
    const renderLevel = () => {
      snapshotDirty=true;
      catalog.querySelectorAll('.rh-level-path,.rh-level-current,.rh-level-parent').forEach(node => node.classList.remove('rh-level-path','rh-level-current','rh-level-parent'));
      activeBranch = branchStack.at(-1)?.branch;
      catalog.classList.toggle('is-submenu', Boolean(activeBranch));
      branchStack.forEach(({branch}, index) => {
        branch.classList.add('rh-level-path');
        if (index === branchStack.length - 1) branch.classList.add('rh-level-current');
        else branch.querySelector(':scope > .rh-menu-children')?.classList.add('rh-level-parent');
      });
      sheetTitle.textContent = activeBranch ? activeBranch.dataset.rhMenuTitle || activeBranch.querySelector(':scope > summary').textContent.trim() : defaultTitle;
      backButton.setAttribute('aria-label', activeBranch ? catalog.dataset.backLabel : defaultBackLabel);
    };
    const backToCards = (focus = true) => {
      const first = branchStack[0];
      branchStack.splice(0).forEach(({branch}) => { branch.open = false; });
      renderLevel();
      if (first) { sheetBody.scrollTop = first.scroll; if (focus) first.branch.querySelector('summary')?.focus({preventScroll:true}); }
    };
    const backOneLevel = () => {
      const entry = branchStack.pop();
      if (!entry) return;
      entry.branch.open = false;
      renderLevel();
      sheetBody.scrollTop = entry.scroll;
      entry.branch.querySelector('summary')?.focus({preventScroll:true});
    };
    const hydratedSources = new WeakMap();
    const hydrateMenu = () => {
      backToCards(false);
      let found = false;
      catalog.querySelectorAll('[data-rh-menu-target]').forEach(target => {
        const source = document.querySelector(`template[data-rh-menu-source="${target.dataset.rhMenuTarget}"]`);
        if (source) { if(hydratedSources.get(target)!==source){target.replaceChildren(source.content.cloneNode(true));hydratedSources.set(target,source);} found = true; }
      });
      const fallback = catalog.querySelector('[data-rh-menu-fallback]');
      if (fallback) fallback.hidden = found;
      catalog.classList.toggle('has-menu-tree',found);
    };
    sheetBody.addEventListener('scroll',()=>{snapshotDirty=true;},{passive:true,signal});
    menuTabs.forEach(button => button.addEventListener('click',() => {
      snapshotDirty=true;
      backToCards(false);
      menuTabs.forEach(tab => tab.setAttribute('aria-pressed',String(tab === button)));
      catalog.querySelectorAll('[data-rh-menu-target]').forEach(target => { target.hidden = target.dataset.rhMenuTarget !== button.dataset.rhMenuTab; });
    },{signal}));
    catalog.addEventListener('click',event => {
      const summary = event.target.closest('summary');
      const branch = summary?.parentElement;
      if (!branch?.matches('.rh-menu-branch') || !branch.closest('[data-rh-menu-target="catalog"]')) return;
      event.preventDefault();
      branchStack.push({branch, scroll:sheetBody.scrollTop});
      branch.open = true;
      renderLevel();
      sheetBody.scrollTop = 0;
      branch.querySelector(':scope > .rh-menu-children > a')?.focus({preventScroll:true});
    },{signal});
    const catalogSurface = catalog.querySelector('.rh-catalog__surface');
    const stopMotion = () => {
      motionVersion++; cancelAnimationFrame(motionFrame); motionCanvas?.remove();motionCanvas=null;
      catalog.classList.remove('is-mesh-moving');
    };
    const setCatalogOrigin = opener => {
      const box=catalog.getBoundingClientRect();
      const icon=(opener.querySelector('svg')||opener).getBoundingClientRect();
      launchTarget={left:icon.left-box.left,right:icon.right-box.left,top:icon.top-box.top,bottom:icon.bottom-box.top,downward:!opener.closest('.rh-bottom-nav')};
    };
    const animateCatalog = async (opening, done) => {
      const previous=motionProgress;
      stopMotion();
      if(matchMedia('(prefers-reduced-motion: reduce)').matches || !launchTarget){done();return;}
      const version=motionVersion;
      const qa=new URLSearchParams(location.search).has('mesh_qa'),captureStart=performance.now();
      const box=catalog.getBoundingClientRect();
      const nav=catalog.querySelector('.rh-bottom-nav').getBoundingClientRect();
      const width=catalogSurface.offsetWidth,height=Math.min(catalogSurface.offsetHeight,nav.top-box.top);
      if(height<1){done();return;}
      // Match high-density mobile screens without unbounded canvas memory.
      const scale=Math.min(2,Math.max(1,window.devicePixelRatio||1));
      catalog.classList.add('is-mesh-moving');
      try {
        if(!motionSource || snapshotDirty){
          const source=await catalogSnapshot(catalogSurface,width,height,scale);
          if(version!==motionVersion)return;
          motionSource=source;snapshotDirty=false;
        }
        if(version!==motionVersion)return;
        const captureMs=performance.now()-captureStart;
        const canvas=document.createElement('canvas');canvas.className='rh-catalog__mesh';canvas.setAttribute('aria-hidden','true');
        canvas.width=Math.ceil(width*scale);canvas.height=Math.ceil(catalog.offsetHeight*scale);
        canvas.style.width=width+'px';canvas.style.height=catalog.offsetHeight+'px';
        catalog.append(canvas);motionCanvas=canvas;
        const ctx=canvas.getContext('2d');ctx.scale(scale,scale);
        ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';
        const rows=Math.ceil(height),start=performance.now(),from=opening?0:previous,to=opening?1:0;
        const minimize=false; // Closing retraces the same shape and timing in reverse.
        const dock={x:(launchTarget.left+launchTarget.right)/2,y:(launchTarget.top+launchTarget.bottom)/2,width:launchTarget.right-launchTarget.left,height:6};
        const downward=launchTarget.downward;
        const rowDock=downward?{...dock,y:height-dock.y}:dock;
        let frames=0,maxPaint=0,maxGap=0,last=start;
        const duration=500*Math.abs(to-from);
        const tick=now=>{
          if(version!==motionVersion)return;
          const paintStart=performance.now();
          maxGap=Math.max(maxGap,now-last);last=now;
          const time=Math.min(1,(now-start)/Math.max(1,duration));
          motionProgress=from+(to-from)*time;
          const raw=minimize?1-motionProgress:motionProgress;
          ctx.clearRect(0,0,width,canvas.height/scale);
          let point=genieRow(raw,minimize,0,width,height,rowDock);
          for(let row=0;row<rows;row++){
            const next=genieRow(raw,minimize,row+1,width,height,rowDock);
            if(point.width<.8){point=next;continue;}
            const rowHeight=Math.max(.01,next.y-point.y);
            const sourceRow=downward?rows-1-row:row;
            const destY=downward?height-next.y:point.y;
            ctx.drawImage(motionSource,0,sourceRow*scale,motionSource.width,scale,point.left,destY,point.width,rowHeight+.35);
            point=next;
          }
          const glow=minimize?raw:1-raw;
          if(glow>.75){
            const progress=(glow-.75)/.25,alpha=(1-(1-progress)*(1-progress))*.3;
            const gradient=ctx.createRadialGradient(dock.x,dock.y,0,dock.x,dock.y,55);
            gradient.addColorStop(0,`rgba(255,255,255,${alpha})`);gradient.addColorStop(1,'rgba(255,255,255,0)');
            ctx.fillStyle=gradient;ctx.fillRect(0,0,width,canvas.height/scale);
          }
          frames++;maxPaint=Math.max(maxPaint,performance.now()-paintStart);
          if(time<1)motionFrame=requestAnimationFrame(tick);
          else {
            if(qa)catalog.dataset.meshQa=JSON.stringify({opening,direction:downward?'down':'up',frames,captureMs:Math.round(captureMs),maxPaintMs:+maxPaint.toFixed(2),maxGapMs:+maxGap.toFixed(2),rows});
            stopMotion();done();
          }
        };
        tick(start);motionFrame=requestAnimationFrame(tick);
      } catch {if(version===motionVersion){stopMotion();done();}}
    };
    const expanded = value => document.querySelectorAll('[data-rh-catalog]').forEach(link => link.setAttribute('aria-expanded',String(value)));
    const close = (immediate = false, focus = true) => {
      if (!catalog.open) return;
      const finish = () => {
        clearTimeout(fallback); closing = false;
        stopMotion();motionSource=null;motionProgress=0;
        catalog.close(); catalog.classList.remove('is-closing');
        document.documentElement.classList.remove('rh-catalog-lock');
        expanded(false);
        if (focus) trigger?.focus({preventScroll:true});
        overlayChanged();
      };
      if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return; }
      if (closing) return;
      closing = true; catalog.classList.add('is-closing');
      animateCatalog(false,finish);
      fallback=setTimeout(finish,1000);
    };
    document.addEventListener('click',event => {
      const opener = event.target.closest('[data-rh-catalog]');
      if (opener && mobile.matches) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (catalog.open || closing) { close(); return; }
        dismissSearch?.();
        trigger = opener; hydrateMenu(); motionSource=null;motionProgress=0;catalog.showModal();setCatalogOrigin(opener);
        animateCatalog(true,()=>{});
        document.documentElement.classList.add('rh-catalog-lock');
        expanded(true); overlayChanged(); return;
      }
      if (catalog.open && event.target.closest('[data-rh-sheet-nav] a')) {
        const home = event.target.closest('a').getAttribute('href') === '/';
        if (home) event.preventDefault();
        close(true,home);
      }
    }, {capture:true,signal});
    catalog.addEventListener('shopify:block:select',() => {
      if (catalog.open) return;
      hydrateMenu(); catalog.showModal(); document.documentElement.classList.add('rh-catalog-lock'); expanded(true); overlayChanged();
    },{signal});
    catalog.addEventListener('shopify:block:deselect',() => close(true,false),{signal});
    catalog.querySelectorAll('[data-rh-catalog-close]').forEach(button => button.addEventListener('click',() => { if (button.classList.contains('rh-back') && activeBranch) backOneLevel(); else close(); },{signal}));
    catalog.addEventListener('cancel',event => { event.preventDefault(); if (activeBranch) backOneLevel(); else close(); },{signal});
    catalog.addEventListener('click',event => {
      if (event.target !== catalog) return;
      const box = catalog.getBoundingClientRect();
      if (event.clientY < box.top || event.clientX < box.left || event.clientX > box.right) close();
    },{signal});
    mobile.addEventListener('change',() => { if (!mobile.matches) close(true); },{signal});
    catalogController = {element:catalog,close,destroy:() => { close(true,false); clearTimeout(fallback); lifecycle.abort(); }};
  }
  function init() {
    initCarousel();
    initCatalog();
    const host = document.querySelector('[data-rh-entry]');
    if (!host) { searchCleanup?.(); searchCleanup = null; return; }
    if (host.dataset.ready) return;
    searchCleanup?.();
    const searchLifecycle = new AbortController();
    host.dataset.ready = 'true';
    const dialog = host.querySelector('[data-rh-dialog]');
    const opener = host.querySelector('[data-rh-open]');
    const bottomSearchClass = 'rh-bottom-search-lock';
    const query = dialog.querySelector('[data-rh-query]');
    const defaults = dialog.querySelector('[data-rh-default]');
    const results = dialog.querySelector('[data-rh-results]');
    const status = dialog.querySelector('[data-rh-status]');
    const all = dialog.querySelector('[data-rh-all]');
    const tabs = dialog.querySelector('[data-rh-tabs]');
    const cache = new Map();
    let resultType = 'all';
    const moreLabels = Object.fromEntries(['product','collection','article','page'].map(type => [type,dialog.dataset['all' + type[0].toUpperCase() + type.slice(1)]]));
    const labels = Object.fromEntries([...tabs.querySelectorAll('[data-rh-type]')].map(button => [button.dataset.rhType,button.textContent]));
    const selectType = type => {
      const groups = [...results.querySelectorAll('[data-search-group]')];
      const available = new Set(groups.filter(group => Number(group.dataset.total) > 0).map(group => group.dataset.searchGroup));
      if (type !== 'all' && !available.has(type)) type = 'all';
      resultType = type;
      for (const key of ['all','product','collection','article','page']) {
        const button = tabs.querySelector('[data-rh-type="' + key + '"]');
        if (!button) continue;
        button.hidden = key !== 'all' && !available.has(key);
        button.setAttribute('aria-pressed',String(key === type));
        tabs.append(button);
      }
      tabs.hidden = available.size === 0;
      groups.forEach(group => { group.hidden = type !== 'all' && group.dataset.searchGroup !== type; });
      all.hidden = available.size === 0;
      all.href = fullURL(query.value.trim());
    };
    tabs.addEventListener('click',event => { const button = event.target.closest('[data-rh-type]'); if (button) selectType(button.dataset.rhType); });
    let timer, controller, closeTimer, revision = 0, returnFocus, restoringFocus = false;
    const fullURL = value => {
      const url = new URL(dialog.dataset.searchUrl, location.origin);
      url.searchParams.set('view', 'royal-discovery');
      url.searchParams.set('q', value);
      return url;
    };
    const resetRequest = () => {
      clearTimeout(timer);
      controller?.abort();
      revision++;
      results.removeAttribute('aria-busy');
    };
    searchCleanup = () => { clearTimeout(closeTimer); resetRequest(); if (dialog.open) dialog.close(); searchLifecycle.abort(); document.documentElement.classList.remove('rh-search-lock', bottomSearchClass); };
    const syncSearchViewport = () => {
      if (!dialog.classList.contains("rh-dialog--bottom")) return;
      const viewport = window.visualViewport;
      dialog.style.setProperty("--rh-search-height", `${viewport?.height || innerHeight}px`);
      dialog.style.setProperty("--rh-search-top", `${viewport?.offsetTop || 0}px`);
    };
    window.visualViewport?.addEventListener("resize", syncSearchViewport, {signal:searchLifecycle.signal});
    window.visualViewport?.addEventListener("scroll", syncSearchViewport, {signal:searchLifecycle.signal});
    let recentTerms = [];
    try { recentTerms = JSON.parse(localStorage.getItem('royal-search-recent') || '[]'); if (!Array.isArray(recentTerms)) recentTerms = []; } catch (_) {}
    const rememberSearch = value => {
      value = value.trim(); if (!value) return;
      recentTerms = [value, ...recentTerms.filter(term => term !== value)].slice(0, 6);
      try { localStorage.setItem('royal-search-recent', JSON.stringify(recentTerms)); } catch (_) {}
    };
    const showRecent = () => {
      const box = dialog.querySelector('[data-rh-recent]'); if (!box) return;
      box.hidden = !recentTerms.length;
      const list = box.querySelector('div'); list.replaceChildren();
      recentTerms.forEach(term => { if (typeof term !== 'string') return; const button = document.createElement('button'); button.type = 'button'; button.dataset.rhTerm = term; button.textContent = term; list.append(button); });
    };
    const open = trigger => {
      if (dialog.open || restoringFocus) return;
      returnFocus = trigger;
      dialog.classList.toggle("rh-dialog--bottom", trigger?.dataset.rhSearchPlacement === "bottom");
      syncSearchViewport();
      closeCatalog(true, false);
      host.querySelectorAll('.rh-language[open]').forEach(detail => { detail.open = false; });
      clearTimeout(closeTimer);dialog.classList.remove('is-closing');
      showRecent();
      document.documentElement.classList.toggle(bottomSearchClass, dialog.classList.contains('rh-dialog--bottom'));
      dialog.showModal();
      overlayChanged();
      document.documentElement.classList.add('rh-search-lock');
      opener.setAttribute('aria-expanded', 'true');
      query.value = opener.value;
      query.placeholder = dialog.classList.contains('rh-dialog--bottom') ? opener.dataset.staticPlaceholder || 'Nə axtarırsınız?' : opener.placeholder;
      query.focus({preventScroll: true});
      if (query.value.trim()) search();
    };
    dismissSearch = () => {
      if (!dialog.open) return;
      restoringFocus = true;
      returnFocus = null;
      query.blur();
      clearTimeout(closeTimer);
      dialog.close();
    };
    const close = () => {
      if(!dialog.open||dialog.classList.contains('is-closing'))return;
      restoringFocus = true;
      query.blur();
      if(matchMedia('(prefers-reduced-motion: reduce)').matches){dialog.close();return}
      dialog.classList.add('is-closing');
      closeTimer=setTimeout(()=>dialog.close(),500);
    };
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    function search() {
      resetRequest();
      const value = query.value.trim();
      defaults.hidden = value.length >= 1;
      results.hidden = value.length < 1;
      tabs.hidden = true;
      if (!value) selectType('all');
      all.hidden = true;
      all.href = fullURL(value);
      results.replaceChildren();
      status.textContent = '';
      if (value.length < 1) return;
      const ticket = revision;
      results.setAttribute('aria-busy', 'true');
      timer = setTimeout(async () => {
        status.textContent = dialog.dataset.loading;
        const url = new URL(dialog.dataset.searchUrl, location.origin);
        url.searchParams.set('view', 'royal-home-suggest');
        url.searchParams.set('q', value);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        controller = new AbortController();
        try {
          let data = cache.get(value);
          if (!data && window.RoyalStatic && window.RoyalDiscovery) {
            data = await window.RoyalStatic.suggest(value,controller.signal);
            cache.set(value,data);
            if (cache.size > 10) cache.delete(cache.keys().next().value);
          }
          if (ticket !== revision || !dialog.open) return;
          if (data) {
            results.replaceChildren(window.RoyalDiscovery.render(data,{allTypes:true,externalTabs:true,type:resultType,labels,query:value,searchUrl:dialog.dataset.searchUrl,moreLabels,empty:dialog.dataset.empty,priceRequest:dialog.dataset.requestPrice}));
          } else {
            const response = await fetch(url,{signal:controller.signal});
            if (!response.ok) throw new Error('search_unavailable');
            const doc = new DOMParser().parseFromString(await response.text(),'text/html');
            const fragment = doc.querySelector('[data-rh-fragment]');
            if (!fragment) throw new Error('missing_results');
            if (ticket !== revision || !dialog.open) return;
            results.innerHTML = fragment.innerHTML;
          }
          selectType(resultType);
          status.textContent = ''; 
        } catch (error) {
          if (error.name !== 'AbortError' && ticket === revision) status.textContent = dialog.dataset.error;
        } finally {
          if (ticket === revision) results.removeAttribute('aria-busy');
        }
      }, 180);
    }
    opener.setAttribute('aria-haspopup', 'dialog');
    opener.setAttribute('aria-expanded', 'false');
    opener.addEventListener('focus', () => open(opener));
    opener.addEventListener('click', () => open(opener));
    opener.closest('form').addEventListener('submit', event => {
      if (!opener.value.trim()) { event.preventDefault(); open(opener); }
    });
    dialog.querySelector('[data-rh-close]').addEventListener('click', close);
    dialog.addEventListener('close', () => {
      clearTimeout(closeTimer);dialog.classList.remove('is-closing');
      resetRequest();
      document.documentElement.classList.remove('rh-search-lock', bottomSearchClass);
      opener.value = query.value;
      opener.setAttribute('aria-expanded', 'false');
      restoringFocus = true;
      returnFocus?.focus({preventScroll:true});
      setTimeout(() => { restoringFocus = false; }, 0);
      overlayChanged();
    });
    dialog.addEventListener('click', event => {
      const term = event.target.closest('[data-rh-term]');
      if (!term) return;
      query.value = term.dataset.rhTerm;
      rememberSearch(query.value);
      query.focus({preventScroll:true});
      search();
    });
    results.addEventListener('click', event => { if (event.target.closest('a')) rememberSearch(query.value); });
    query.addEventListener('input', search);
    opener.dataset.staticPlaceholder = opener.placeholder;
    let typeTimer, word = 0, letter = 0, deleting = false;
    const phrases = ['Cort Elektro gitaraları', 'Roland elektron pianoları', 'Kawai akustik pianoları'];
    const typePlaceholder = () => {
      const placeholderInput = dialog.open ? query : opener;
      if (document.hidden || placeholderInput.value || (dialog.open && dialog.classList.contains('rh-dialog--bottom')) || matchMedia('(prefers-reduced-motion: reduce)').matches) { typeTimer = setTimeout(typePlaceholder, 1200); return; }
      const phrase = phrases[word];
      letter += deleting ? -1 : 1;
      placeholderInput.placeholder = phrase.slice(0, letter);
      let delay = deleting ? 65 : 135;
      if (letter === phrase.length) { deleting = true; delay = 1800; }
      if (letter === 0) { deleting = false; word = (word + 1) % phrases.length; delay = 550; }
      typeTimer = setTimeout(typePlaceholder, delay);
    };
    if (document.documentElement.lang.startsWith('az')) typeTimer = setTimeout(typePlaceholder, 1400);
    searchLifecycle.signal.addEventListener('abort', () => clearTimeout(typeTimer), {once:true});
    query.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown') {
        const first = results.querySelector('.rh-card__title');
        if (first) { event.preventDefault(); first.focus(); }
      }
    });
    dialog.querySelector('form').addEventListener('submit', event => {
      if (!query.value.trim()) { event.preventDefault(); query.focus(); } else rememberSearch(query.value);
    });
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-royal-search-trigger]');
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open(trigger.closest('[data-rh-catalog-panel]') && !trigger.dataset.rhSearchPlacement ? opener : trigger);
    }, {capture:true,signal:searchLifecycle.signal});
    document.querySelectorAll('.rh-cart-count').forEach(badge => {
      new MutationObserver(() => { badge.hidden = !(Number(badge.textContent) > 0); }).observe(badge, {childList:true,characterData:true,subtree:true});
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => queueMicrotask(init));
})();
